import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Avatar,
  Space,
  Typography,
  Input,
  Button,
  Divider,
  Tooltip,
} from "antd";
import { SmileOutlined, SendOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const { Text } = Typography;

const emojiMap = {
  1: "😊",
  2: "😍",
  3: "🎉",
  4: "😄",
  5: "❤️",
};

const emojis = Object.entries(emojiMap).map(([id, emoji]) => ({ id, emoji }));

const EmojiTooltip = ({ onSelect }) => (
  <Space size={8}>
    {emojis.map(({ id, emoji }) => (
      <span
        key={id}
        style={{ fontSize: 18, cursor: "pointer", lineHeight: 1 }}
        onClick={() => onSelect(id)}
      >
        {emoji}
      </span>
    ))}
  </Space>
);

export default function CommentDrawer({
  open,
  onClose,
  user,
  setBirthdayData,
  drawerMessageHandlerRef,
  userInfo,
}) {
  const userList = useSelector(getUserList);
  console.log("userinfo-->", userInfo);
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const userRef = useRef(user); // ✅ always latest user without stale closure

  const [comment, setComment] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});

  const getProfileUrl = (userId, userName) => {
    const found = userList?.find((u) => u.userid === userId);
    return (
      found?.profileurl ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(userName || "")}`
    );
  };

  // Keep userRef fresh
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleReplies = (msgId) => {
    setExpandedReplies((prev) => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  // ── Register realtime handler with parent WebSocket ──────────────────────
  useEffect(() => {
    if (!drawerMessageHandlerRef) return;

    // This function is called by parent's WebSocket onmessage
    drawerMessageHandlerRef.current = (payload) => {
      const currentUser = userRef.current;

      // ── NEW_MESSAGE ──
      if (payload.type === "NEW_MESSAGE") {
        if (payload.data?.employee_id === currentUser?.employeeId) {
          setMessages((prev) => {
            const exists = prev.find((m) => m.id === payload.data.id);
            if (exists) return prev;
            return [...prev, { ...payload.data, replies: [], reactions: [] }];
          });
        }
      }

      // ── NEW_REPLY ──
      if (payload.type === "NEW_REPLY") {
        if (payload.data?.employee_id === currentUser?.employeeId) {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== payload.data.message_id) return m;
              const exists = (m.replies || []).find(
                (r) => r.id === payload.data.id
              );
              if (exists) return m;
              return { ...m, replies: [...(m.replies || []), payload.data] };
            })
          );
        }
      }

      // ── REACTION_UPDATE (message level) ──
      if (payload.type === "REACTION_UPDATE" && payload.messageId) {
        const normalized = (payload.groupedReactions || []).map((r) => ({
          emoji: r.emoji || r.reaction,
          count: parseInt(r.count || 1, 10),
        }));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === payload.messageId ? { ...m, reactions: normalized } : m
          )
        );
      }
    };

    // Cleanup handler on unmount
    return () => {
      if (drawerMessageHandlerRef.current) {
        drawerMessageHandlerRef.current = null;
      }
    };
  }, [drawerMessageHandlerRef]);

  // ── Fetch messages ───────────────────────────────────────────────────────
  const fetchMessages = async () => {
    if (!user?.employeeId) return;
    try {
      setLoading(true);
      const { code, data } = await CALL_API(`chat`, "post", {
        action: "getMessages",
        employeeId: user.employeeId,
      });
      if (code === STATUS_CODE.SUCCESS) {
        const normalized = (data || []).map((msg) => ({
          ...msg,
          replies: (msg.replies || []).map((r) => ({ ...r, reactions: [] })),
          reactions: (msg.reactions || []).map((r) => ({
            emoji: r.emoji || r.reaction,
            count: parseInt(r.count || 1, 10),
          })),
        }));
        setMessages(normalized);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error("Fetch messages error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Send main message ────────────────────────────────────────────────────
  // ── Send main message ────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!comment.trim() || sendingMsg) return;
    try {
      setSendingMsg(true);
      const { code, data } = await CALL_API(`chat`, "post", {
        action: "sendMessage",
        employeeId: user?.employeeId, // ✅ birthday person's ID (from selectedUser)
        message: comment.trim(),
        userId: userInfo?.userid, // ✅ "alanalan@gmail.com"
        userName: userInfo?.name, // ✅ "Albert Alan"
      });
      if (code === STATUS_CODE.SUCCESS) {
        // setMessages((prev) => [
        //   ...prev,
        //   { ...data, replies: [], reactions: [] },
        // ]);
        setComment("");
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error("Send error:", err);
    } finally {
      setSendingMsg(false);
    }
  };

  // ── Send reply ───────────────────────────────────────────────────────────
  const sendReply = async (msgId) => {
    if (!replyText.trim() || sendingReply) return;
    try {
      setSendingReply(true);
      const { code, data } = await CALL_API(`chat`, "post", {
        action: "sendReply",
        parentMessageId: msgId,
        message: replyText.trim(),
        userId: userInfo?.userid, // ✅ "alanalan@gmail.com"
        userName: userInfo?.name, // ✅ "Albert Alan"
        employeeId: user?.employeeId, // ✅ birthday person's ID
      });
      if (code === STATUS_CODE.SUCCESS) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, replies: [...(m.replies || []), data] } : m
          )
        );
        setExpandedReplies((prev) => ({ ...prev, [msgId]: true }));
        setReplyText("");
        setReplyingTo(null);
      }
    } catch (err) {
      console.error("Reply error:", err);
    } finally {
      setSendingReply(false);
    }
  };

  // ── Send reaction ────────────────────────────────────────────────────────
  const sendReaction = async (emojiId, msgId) => {
    const reaction = emojiMap[emojiId];
    if (!reaction) return;

    // Optimistic update
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msgId) return m;
        const existing = (m.reactions || []).find(
          (r) => (r.emoji || r.reaction) === reaction
        );
        const updatedReactions = existing
          ? m.reactions.map((r) =>
              (r.emoji || r.reaction) === reaction
                ? { emoji: reaction, count: parseInt(r.count || 1, 10) + 1 }
                : {
                    emoji: r.emoji || r.reaction,
                    count: parseInt(r.count || 1, 10),
                  }
            )
          : [
              ...m.reactions.map((r) => ({
                emoji: r.emoji || r.reaction,
                count: parseInt(r.count || 1, 10),
              })),
              { emoji: reaction, count: 1 },
            ];
        return { ...m, reactions: updatedReactions };
      })
    );

    try {
      const { code, groupedReactions } = await CALL_API(`chat`, "post", {
        action: "reactMessage",
        messageId: msgId,
        userId: userInfo?.userid, // ✅ "alanalan@gmail.com"
        reaction,
      });
      if (code === STATUS_CODE.SUCCESS && groupedReactions) {
        const normalized = groupedReactions.map((r) => ({
          emoji: r.emoji || r.reaction,
          count: parseInt(r.count || 1, 10),
        }));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, reactions: normalized } : m
          )
        );
      }
    } catch (err) {
      console.error("Reaction error:", err);
      fetchMessages();
    }
  };

  const handleReply = (msg) => {
    if (replyingTo?.msgId === msg.id) {
      setReplyingTo(null);
      setReplyText("");
    } else {
      setReplyingTo({ msgId: msg.id, user_name: msg.user_name });
      setReplyText(`@${msg.user_name} `);
    }
  };

  const handleCloseReply = () => {
    setReplyingTo(null);
    setReplyText("");
  };

  // ── Fetch on open / reset on close ──────────────────────────────────────
  useEffect(() => {
    if (open && user?.employeeId) {
      fetchMessages();
    }
    if (!open) {
      setMessages([]);
      setComment("");
      setReplyingTo(null);
      setReplyText("");
      setExpandedReplies({});
    }
  }, [open, user]);

  // ── Format time ──────────────────────────────────────────────────────────
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <StyledDrawer
      title={t("Messages")}
      placement="right"
      width={window.innerWidth < 768 ? "100%" : 420}
      open={open}
      onClose={onClose}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          height: "100%",
          overflow: "hidden",
        },
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
        {loading ? (
          <Text type="secondary">{t("Loading...")}</Text>
        ) : messages.length === 0 ? (
          <Text type="secondary">{t("No messages yet. Be the first!")}</Text>
        ) : (
          messages.map((msg) => {
            const isReplying = replyingTo?.msgId === msg.id;
            const isExpanded = !!expandedReplies[msg.id];
            const hasReplies = msg.replies?.length > 0;
            const hasReactions = msg.reactions?.length > 0;

            return (
              <div key={msg.id}>
                <div style={{ display: "flex", gap: 10 }}>
                  <Avatar
                    src={getProfileUrl(msg.user_id, msg.user_name)}
                    size={36}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  >
                    {msg.user_name?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Space size={6} align="center">
                      <Text strong style={{ fontSize: 13 }}>
                        {msg.user_name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatTime(msg.created_at)}
                      </Text>
                    </Space>

                    <div
                      style={{
                        fontSize: 13,
                        marginTop: 2,
                        wordBreak: "break-word",
                      }}
                    >
                      {msg.message}
                    </div>

                    {(hasReactions || hasReplies) && (
                      <Space
                        size={6}
                        align="center"
                        style={{ marginTop: 6, flexWrap: "wrap" }}
                      >
                        {hasReactions &&
                          msg.reactions.map((r, i) => (
                            <Button
                              key={i}
                              size="small"
                              style={{
                                borderRadius: 12,
                                padding: "0 8px",
                                fontSize: 12,
                                height: 24,
                              }}
                            >
                              {r.emoji} {r.count}
                            </Button>
                          ))}

                        {hasReplies && (
                          <Space
                            size={4}
                            align="center"
                            style={{ cursor: "pointer" }}
                            onClick={() => toggleReplies(msg.id)}
                          >
                            <Avatar.Group maxCount={3} size={20}>
                              {msg.replies.map((r, i) => (
                                <Avatar
                                  key={i}
                                  size={20}
                                  src={getProfileUrl(r.user_id, r.user_name)}
                                >
                                  {r.user_name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                              ))}
                            </Avatar.Group>
                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                              {msg.replies.length}+ {t("replies")}
                            </Text>
                          </Space>
                        )}
                      </Space>
                    )}

                    {hasReplies && isExpanded && (
                      <div
                        style={{
                          marginTop: 8,
                          background: "#f5f7fa",
                          borderRadius: 8,
                          padding: "8px 10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {msg.replies.map((reply) => (
                          <div
                            key={reply.id}
                            style={{ display: "flex", gap: 8 }}
                          >
                            <Avatar size={28} style={{ flexShrink: 0 }}>
                              {reply.user_name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <div style={{ flex: 1 }}>
                              <Space size={6} align="center">
                                <Text strong style={{ fontSize: 12 }}>
                                  {reply.user_name}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {formatTime(reply.created_at)}
                                </Text>
                              </Space>
                              <div
                                style={{
                                  fontSize: 12,
                                  marginTop: 2,
                                  wordBreak: "break-word",
                                  color: "#333",
                                }}
                              >
                                {reply.message}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {isReplying && (
                      <div
                        style={{
                          marginTop: 8,
                          background: "#f5f5f5",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <Input
                          autoFocus
                          placeholder={t("Type here...")}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onPressEnter={() => sendReply(msg.id)}
                          style={{ borderRadius: 20 }}
                          suffix={
                            <Space size={4}>
                              <Button
                                type="primary"
                                shape="circle"
                                size="small"
                                icon={<SendOutlined />}
                                loading={sendingReply}
                                onClick={() => sendReply(msg.id)}
                              />
                              <Button
                                shape="circle"
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={handleCloseReply}
                              />
                            </Space>
                          }
                        />
                      </div>
                    )}
                  </div>

                  <Space
                    size={10}
                    style={{
                      flexShrink: 0,
                      alignSelf: "flex-start",
                      marginTop: 4,
                    }}
                  >
                    <Tooltip
                      trigger="click"
                      placement="top"
                      title={
                        <EmojiTooltip
                          onSelect={(id) => sendReaction(id, msg.id)}
                        />
                      }
                    >
                      <SmileOutlined
                        style={{
                          fontSize: 16,
                          color: "#aaa",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                    <Text
                      style={{
                        fontSize: 13,
                        color: "#1677ff",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => handleReply(msg)}
                    >
                      {t("Reply")}
                    </Text>
                  </Space>
                </div>
                <Divider style={{ margin: "10px 0" }} />
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div
        style={{
          borderTop: "1px solid #f0f0f0",
          padding: "10px 16px",
          background: "#fff",
        }}
      >
        <Input
          placeholder={t("Type here...")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onPressEnter={sendMessage}
          style={{ borderRadius: 20 }}
          suffix={
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              loading={sendingMsg}
              onClick={sendMessage}
            />
          }
        />
      </div>
    </StyledDrawer>
  );
}
